import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Star, 
  TrendingUp, 
  RefreshCw,
  Target,
  Award,
  Clock,
  Users,
  Briefcase,
  Info,
  Mail,
  FileText,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ResponsiveContainer } from '../ui/ResponsiveContainer';
import { EdgeActions } from '../ui/EdgeActions';
import { apiClient } from '../../lib/api/client';
import { usePDFGeneration } from '../../lib/hooks/usePDFGeneration';
import { GenerateCallAnalysisButton } from '../calls/GenerateCallAnalysisButton';
import { StatusUpdateModal } from './StatusUpdateModal';

interface AnalysisResults {
  overall_fitment_score: number;
  parameter_analysis: {
    skills: ParameterAnalysis;
    experience: ParameterAnalysis;
    education: ParameterAnalysis;
    optional_skills: ParameterAnalysis;
  };
  recommendations: string[];
  strengths: string[];
  concerns: string[];
  analysis_results?: {
    cold_call_script?: {
      objections_handling: string;
      opening: string;
      discovery: string[];
      value_proposition: string;
      next_steps: string;
    };
    form_data?: {
      name?: string;
      phone?: string;
      email?: string;
      location?: string;
      [key: string]: any;
    };
    [key: string]: any;
  };
  cold_call_script?: {
    objections_handling: string;
    opening: string;
    discovery: string[];
    value_proposition: string;
    next_steps: string;
  };
  form_data?: {
    name?: string;
    phone?: string;
    email?: string;
    location?: string;
    [key: string]: any;
  };
}

interface ParameterAnalysis {
  score: number;
  weight: number;
  explanation: string;
  matched_skills?: string[];
  missing_skills?: string[];
  skills_match?: string[];
  years_match?: boolean;
  domain_match?: boolean;
  degree_match?: boolean;
  institution_tier?: string;
  location_match?: boolean;
  remote_work?: boolean;
  salary_match?: boolean;
  expectation_gap?: string;
  communication_skills?: string;
  team_work?: string;
}

export const EnhancedJDCVAnalysisPage: React.FC = () => {
  const { candidateId, requirementId } = useParams<{ candidateId: string; requirementId: string }>();
  const navigate = useNavigate();
  
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [candidateData, setCandidateData] = useState<any>(null);
  const [requirementData, setRequirementData] = useState<any>(null);
  const [showWeightInfo, setShowWeightInfo] = useState(false);
  const [skillsWeightAllocation, setSkillsWeightAllocation] = useState<Array<{ skill: string; type: string; weight_percent: number }>>([]);
  const [skillsTableRows, setSkillsTableRows] = useState<Array<{ jd_skill_name: string; type: 'mandatory' | 'optional'; cv_match: boolean; cv_skill_name?: string | null; experience?: string | null; score: number; level?: string | null }>>([]);
  const [fitmentJustification, setFitmentJustification] = useState<string | null>(null);
  const [experienceAssessment, setExperienceAssessment] = useState<string | null>(null);
  const [overallRecommendation, setOverallRecommendation] = useState<string | null>(null);
  const [salaryNegotiation, setSalaryNegotiation] = useState<string | null>(null);
  const [screeningQuestions, setScreeningQuestions] = useState<string[]>([]);
  // Removed compact view toggle - now single view
  
  // Status update modal state
  const [statusUpdateModalOpen, setStatusUpdateModalOpen] = useState(false);
  const [selectedStatusForUpdate, setSelectedStatusForUpdate] = useState<string>('Uploaded');
  const [currentSubmissionStatus, setCurrentSubmissionStatus] = useState<string>('Uploaded');
  const [triggeringAnalysis, setTriggeringAnalysis] = useState(false);
  
  // PDF Generation
  const { generatePDF, isGenerating } = usePDFGeneration();
  
  // PDF Generation Handler
  const handleGeneratePDF = async () => {
    if (!candidateId || !requirementId) {
      toast.error('Missing candidate or requirement ID for PDF generation');
      return;
    }

    try {
      await generatePDF(candidateId, requirementId, {
        includeSensitiveData: false, // Default to false for client sharing
        reportFormat: 'comprehensive'
      });
    } catch (error) {
      console.error('PDF generation failed:', error);
    }
  };

  useEffect(() => {
    if (candidateId && requirementId) {
      fetchAnalysisResults();
    }
  }, [candidateId, requirementId]);

  const fetchAnalysisResults = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get analysis results using the same method as CandidateAnalysisPage
      const results = await apiClient.getCandidateAnalysisResults(candidateId!, requirementId!);
      console.log('📊 Enhanced analysis results:', results);
      
      if (results && results.analysis_results && results.analysis_results.analysis) {
        // Transform the data to match our expected format
        const analysis = results.analysis_results.analysis;
        const skillMatchLocal = results.analysis_results.skill_match_analysis || {};
        const expAnalysis = results.analysis_results.experience_analysis || {};
        const eduAnalysis = results.analysis_results.education_analysis || {};
        const transformedResults: AnalysisResults = {
          overall_fitment_score: analysis.overall_fitment_score,
          parameter_analysis: {
            skills: {
              score: analysis.skill_match_score,
              weight: 0.3,
              explanation: skillMatchLocal.explanation || 'Skills analysis completed',
              matched_skills: [
                ...(skillMatchLocal.mandatory_skills_match || []),
                ...(skillMatchLocal.optional_skills_match || [])
              ],
              missing_skills: [
                ...(skillMatchLocal.missing_mandatory_skills || []),
                ...(skillMatchLocal.missing_optional_skills || [])
              ]
            },
            experience: {
              score: analysis.experience_match_score,
              weight: 0.25,
              explanation: expAnalysis.explanation || 'Experience analysis completed',
              years_match: (typeof expAnalysis.candidate_experience_years === 'number' && typeof expAnalysis.required_experience_years === 'number')
                ? expAnalysis.candidate_experience_years >= expAnalysis.required_experience_years
                : expAnalysis.experience_assessment !== 'underqualified',
              domain_match: typeof expAnalysis.domain_experience_match === 'boolean' ? expAnalysis.domain_experience_match : undefined
            },
            education: {
              score: analysis.education_match_score,
              weight: 0.15,
              explanation: eduAnalysis.score_reason || eduAnalysis.explanation || 'Education analysis completed',
              degree_match: typeof eduAnalysis.education_level_match === 'boolean' ? eduAnalysis.education_level_match : undefined,
              institution_tier: eduAnalysis.institution_tier || (typeof eduAnalysis.institution_tier_match === 'boolean' ? (eduAnalysis.institution_tier_match ? 'Tier match' : 'Tier mismatch') : undefined)
            },
            optional_skills: {
              score: analysis.optional_skills_score || 0,
              weight: 0.15,
              explanation: 'Optional skills analysis completed',
              skills_match: skillMatchLocal.optional_skills_match || [],
              missing_skills: skillMatchLocal.missing_optional_skills || []
            }
          },
          recommendations: results.analysis_results.recruiter_insights?.recommendations || [],
          strengths: results.analysis_results.strengths || [],
          concerns: results.analysis_results.concerns || []
        };
        
        setAnalysisResults(transformedResults);

        // Map new API fields for enhanced UI
        const skillMatch = results.analysis_results.skill_match_analysis || {};
        setSkillsWeightAllocation(skillMatch.skills_weight_allocation || []);
        setSkillsTableRows(skillMatch.skills_table_rows || []);
        setFitmentJustification(results.analysis_results.fitment_justification || null);
        const exp = results.analysis_results.experience_analysis || {};
        setExperienceAssessment(exp.experience_assessment || null);
        
        // Use AI-generated candidate summary
        const aiSummary = results.analysis_results.candidate_summary;
        
        // Extract additional API data
        setOverallRecommendation(results.analysis_results.recommendation?.overall_recommendation || null);
        setSalaryNegotiation(results.analysis_results.recommendation?.salary_negotiation || null);
        const rq = results.analysis_results.recruiter_insights?.technical_screening_questions || [];
        const sq = results.analysis_results.screening_questions?.l1_technical || [];
        setScreeningQuestions((rq && rq.length ? rq : sq) || []);
        
        // Use form_data from API response instead of separate API calls
        const formData = results.analysis_results.form_data || {};
        console.log('🔍 Frontend Debug - formData.work_experience:', formData.work_experience);
        setCandidateData({
          name: formData.name || 'N/A',
          email: formData.email || 'N/A',
          phone: formData.phone || 'N/A',
          location: formData.location || 'N/A',
          current_designation: formData.current_position || 'N/A',
          current_company: formData.current_company || 'N/A',
          experience: formData.experience_years ? `${formData.experience_years} years` : 'N/A',
          education: formData.education || 'N/A',
          summary: aiSummary || `Experienced professional with ${formData.experience_years || 0} years of experience in ${formData.current_position || 'various roles'}. Strong background in ${formData.skills?.join(', ') || 'multiple domains'}.`,
          skills: formData.skills || [],
          work_experience: formData.work_experience || [],
          certifications: formData.certifications || [],
          languages: formData.languages || [],
          availability: formData.availability || 'N/A'
        });
        
        // Set current submission status from results
        const submissionStatus = results.submission_status || results.status || 'Uploaded';
        setCurrentSubmissionStatus(submissionStatus);
        setSelectedStatusForUpdate(submissionStatus);
        
        // Get requirement data for display
        try {
          const requirement = await apiClient.getRequirementDetails(requirementId!);
          setRequirementData(requirement);
        } catch (err) {
          console.warn('Failed to fetch requirement details:', err);
          // Use metadata if available
          setRequirementData({
            title: results.analysis_results.analysis_metadata?.job_title || 'Position',
            company: 'Company',
            location: 'Location',
            description: 'Job description not available'
          });
        }
      } else {
        // Check if this is a submission without analysis
        if (results.status === 'submitted' || results.status === 'pending') {
          setError('Analysis has not been completed yet. Click "Trigger Analysis" to start the analysis process.');
        } else {
          setError('Analysis results not found. Please try again.');
        }
      }
    } catch (err) {
      console.error('Failed to fetch analysis results:', err);
      setError('Failed to load analysis results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await fetchAnalysisResults();
      toast.success('Analysis results refreshed!');
    } catch (err) {
      toast.error('Failed to refresh analysis results');
    } finally {
      setRetrying(false);
    }
  };

  const handleDownloadPDF = async () => {
    await handleGeneratePDF();
  };

  const handleSendJDEmail = async () => {
    try {
      toast.success('JD email will be sent soon!');
    } catch (err) {
      toast.error('Failed to send JD email');
    }
  };

  const handleDownloadResume = async () => {
    try {
      if (!candidateId) return;
      const { url } = await apiClient.getCandidateResumeUrl(candidateId, { disposition: 'attachment', requirementId: requirementId || undefined });
      const a = document.createElement('a');
      a.href = url;
      a.download = '';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error('Failed to download resume');
    }
  };

  const handleOpenResume = async () => {
    try {
      if (!candidateId) return;
      const { url } = await apiClient.getCandidateResumeUrl(candidateId, { disposition: 'inline', requirementId: requirementId || undefined });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error('Failed to open resume');
    }
  };


  const handleTriggerAnalysis = async () => {
    if (!candidateId || !requirementId) return;
    
    setTriggeringAnalysis(true);
    try {
      await apiClient.triggerAnalysis(candidateId, requirementId);
      toast.success('Analysis triggered successfully! Please wait a moment and refresh.');
      // Wait 2 seconds then refresh
      setTimeout(() => {
        fetchAnalysisResults();
      }, 2000);
    } catch (err) {
      console.error('Failed to trigger analysis:', err);
      toast.error('Failed to trigger analysis. Please try again.');
    } finally {
      setTriggeringAnalysis(false);
    }
  };

  const handleUpdateStatus = (status: string) => {
    setSelectedStatusForUpdate(status);
    setStatusUpdateModalOpen(true);
  };

  const handleShowCallScript = () => {
    // Navigate to the new call script page with script data
    const sessionId = `session_${candidateId}_${requirementId}_${Date.now()}`;
    
    console.log('🔍 EnhancedJDCVAnalysisPage: handleShowCallScript called');
    console.log('🔍 EnhancedJDCVAnalysisPage: analysisResults:', analysisResults);
    console.log('🔍 EnhancedJDCVAnalysisPage: analysisResults keys:', Object.keys(analysisResults || {}));
    console.log('🔍 EnhancedJDCVAnalysisPage: cold_call_script available:', !!analysisResults?.cold_call_script);
    console.log('🔍 EnhancedJDCVAnalysisPage: analysis_results.analysis_results:', analysisResults?.analysis_results);
    console.log('🔍 EnhancedJDCVAnalysisPage: analysis_results keys:', Object.keys(analysisResults?.analysis_results || {}));
    console.log('🔍 EnhancedJDCVAnalysisPage: nested cold_call_script available:', !!analysisResults?.analysis_results?.cold_call_script);
    
    // Let's also check if the data is in the original response structure
    console.log('🔍 EnhancedJDCVAnalysisPage: Full analysis data structure:', JSON.stringify(analysisResults, null, 2));
    
    // Check multiple possible locations for the call script data
    const callScriptData = analysisResults?.analysis_results?.cold_call_script || 
                          analysisResults?.cold_call_script ||
                          (analysisResults as any)?.cold_call_script;
    
    console.log('🔍 EnhancedJDCVAnalysisPage: callScriptData found:', !!callScriptData);
    console.log('🔍 EnhancedJDCVAnalysisPage: callScriptData:', callScriptData);
    
    // Store the script data in sessionStorage so the call script page can access it
    if (callScriptData) {
      // Transform the call script data into the expected format
      const transformedScriptData = {
        candidate_data: {
          name: analysisResults?.analysis_results?.form_data?.name || 'Candidate',
          phone: analysisResults?.analysis_results?.form_data?.phone || '',
          email: analysisResults?.analysis_results?.form_data?.email || '',
          location: analysisResults?.analysis_results?.form_data?.location || ''
        },
        company_data: {
          name: requirementData?.client_name || 'Company',
          role_title: requirementData?.title || 'Position',
          employment_type: requirementData?.employment_type || 'Full-time',
          location: requirementData?.location || '',
          key_benefits: [],
          company_description: requirementData?.description || '',
          key_skills: []
        },
        recruiter_data: {
          name: 'Recruiter', // This should come from current user
          phone: '' // This should come from current user
        },
        script_data: {
          company_research: {
            company_description: requirementData?.description || '',
            key_benefits: [],
            company_culture: '',
            recent_achievements: [],
            why_work_here: [],
            company_size: '',
            industry: '',
            location: requirementData?.location || ''
          },
          script_phases: [
            {
              id: '1',
              name: 'Opening & Introduction',
              duration: 60,
              content: callScriptData.opening || '',
              questions: ['May I have a moment of your time?', 'Are you open to discussing new opportunities?'],
              is_required: true
            },
            {
              id: '2',
              name: 'Discovery & Qualification',
              duration: 180,
              content: 'Understand candidate\'s current situation and qualifications',
              questions: callScriptData.discovery || [],
              is_required: true
            },
            {
              id: '3',
              name: 'Value Proposition & Next Steps',
              duration: 120,
              content: callScriptData.value_proposition || '',
              questions: [],
              is_required: true
            }
          ]
        },
        total_phases: 3
      };
      
      sessionStorage.setItem(`call_script_${sessionId}`, JSON.stringify(transformedScriptData));
      console.log('🔍 EnhancedJDCVAnalysisPage: Stored script data with key:', `call_script_${sessionId}`);
      console.log('🔍 EnhancedJDCVAnalysisPage: Script data stored:', transformedScriptData);
    } else {
      console.log('🔍 EnhancedJDCVAnalysisPage: No call script data found in analysis results');
      console.log('🔍 EnhancedJDCVAnalysisPage: Available keys in analysisResults:', Object.keys(analysisResults || {}));
      console.log('🔍 EnhancedJDCVAnalysisPage: Available keys in analysis_results:', Object.keys(analysisResults?.analysis_results || {}));
    }
    
    navigate(`/calls/script/${sessionId}`);
  };


  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-700 dark:text-green-300';
    if (score >= 60) return 'text-orange-700 dark:text-orange-300';
    return 'text-red-700 dark:text-red-300';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100 dark:bg-green-900/30';
    if (score >= 60) return 'bg-orange-100 dark:bg-orange-900/30';
    return 'bg-red-100 dark:bg-red-900/30';
  };

  const ringStyle = (score: number, color: string) => {
    const clamped = Math.max(0, Math.min(100, score));
    const deg = clamped * 3.6;
    return {
      background: `conic-gradient(${color} ${deg}deg, rgba(255,255,255,0.08) 0deg)`
    } as React.CSSProperties;
  };

  if (loading) {
    return (
      <ResponsiveContainer>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading analysis results...</p>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (error) {
    const isAnalysisMissing = error.includes('not been completed') || error.includes('not available');
    
    return (
      <ResponsiveContainer>
        <div className="max-w-2xl mx-auto py-8">
          <div className="text-center">
            {isAnalysisMissing ? (
              <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            ) : (
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            )}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {isAnalysisMissing ? 'Analysis Not Started' : 'Analysis Failed'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {isAnalysisMissing && (
                <Button onClick={handleTriggerAnalysis} disabled={triggeringAnalysis}>
                  {triggeringAnalysis ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
                  Trigger Analysis
                </Button>
              )}
              <Button variant="outline" onClick={handleRetry} disabled={retrying}>
                {retrying ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh
              </Button>
              <Button variant="outline" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    );
  }

  if (!analysisResults) {
    return (
      <ResponsiveContainer>
        <div className="text-center py-8">
          <p className="text-gray-600">No analysis results available</p>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <>
      <ResponsiveContainer>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 relative overflow-hidden">
          {/* Glassmorphic background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl"></div>
            {/* Subtle AI particle shimmer */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.06),transparent_40%),radial-gradient(circle_at_80%_30%,rgba(168,85,247,0.06),transparent_40%),radial-gradient(circle_at_50%_80%,rgba(16,185,129,0.06),transparent_40%)] animate-pulse" />
          </div>
          {/* EdgeActions */}
          <EdgeActions
            onCallScript={handleShowCallScript}
            onDownloadPDF={handleDownloadPDF}
            onSendJDEmail={handleSendJDEmail}
            onBack={() => navigate(-1)}
            showCallScript={true}
            showDownloadPDF={true}
            showSendJDEmail={true}
            showBack={true}
            showUpload={false}
            showAdd={false}
            showFilter={false}
            isDownloadingPDF={isGenerating}
          />

          {/* Top Action Bar */}
          <div className="relative z-10 p-6 lg:p-8">
            <div className="glass-card p-6 backdrop-blur-xl space-y-3">
              {/* Row 1: Action Buttons */}
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={handleDownloadPDF} variant="outline" size="sm" disabled={isGenerating}>
                  {isGenerating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                  Generate JD vs CV Report
                </Button>
                <GenerateCallAnalysisButton
                  candidateId={candidateId!}
                  requirementId={requirementId!}
                  candidateName={candidateData?.name}
                  requirementTitle={requirementData?.title}
                  variant="outline"
                />
                <Button onClick={handleSendJDEmail} variant="outline" size="sm">
                  <Mail className="w-4 h-4 mr-2" />
                  Send JD Email
                </Button>
                <Button onClick={handleDownloadResume} variant="outline" size="sm">
                  <FileText className="w-4 h-4 mr-2" />
                  Download Resume
                </Button>
                <Button onClick={handleOpenResume} variant="outline" size="sm">
                  <Eye className="w-4 h-4 mr-2" />
                  Open Resume
                </Button>
              </div>
              
              {/* Row 2: Status Update - Creative single-line design */}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Status:
                </span>
                {/* Creative: Colored dropdown that acts as both badge and selector */}
                <select 
                  value={currentSubmissionStatus}
                  onChange={(e) => handleUpdateStatus(e.target.value)}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg border-2 focus:ring-2 focus:ring-offset-2 focus:outline-none cursor-pointer transition-all hover:shadow-md min-w-[240px] ${
                    currentSubmissionStatus.includes('Select') || currentSubmissionStatus === 'Offered' || currentSubmissionStatus === 'Joined' 
                      ? 'bg-gradient-to-r from-green-50 to-green-100 border-green-400 text-green-800 focus:ring-green-500 dark:from-green-900/40 dark:to-green-800/40 dark:border-green-600 dark:text-green-200'
                      : currentSubmissionStatus.includes('Reject') || currentSubmissionStatus.includes('Drop') || currentSubmissionStatus.includes('CANCELLED')
                      ? 'bg-gradient-to-r from-red-50 to-red-100 border-red-400 text-red-800 focus:ring-red-500 dark:from-red-900/40 dark:to-red-800/40 dark:border-red-600 dark:text-red-200'
                      : currentSubmissionStatus.includes('SCHEDULED') || currentSubmissionStatus.includes('INTERVIEW') || currentSubmissionStatus.includes('TEST')
                      ? 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-400 text-blue-800 focus:ring-blue-500 dark:from-blue-900/40 dark:to-blue-800/40 dark:border-blue-600 dark:text-blue-200'
                      : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300 text-gray-800 focus:ring-gray-500 dark:from-gray-800 dark:to-gray-700 dark:border-gray-600 dark:text-gray-200'
                  }`}
                >
                  <optgroup label="Initial Processing">
                    <option value="Uploaded">Uploaded</option>
                    <option value="Submissions">Submissions</option>
                    <option value="Duplicates">Duplicates</option>
                    <option value="Remaining CVS">Remaining CVS</option>
                    <option value="CV Feedback pending">CV Feedback pending</option>
                    <option value="CV Rejects">CV Rejects</option>
                  </optgroup>
                  <optgroup label="Requirement Management">
                    <option value="Req Closed">Req Closed</option>
                    <option value="REQ HOLD/CANCELLED">REQ HOLD/CANCELLED</option>
                    <option value="REJECT - JD CHANGE">REJECT - JD CHANGE</option>
                  </optgroup>
                  <optgroup label="Assessment & Testing">
                    <option value="TEST LINK SHARED">TEST LINK SHARED</option>
                    <option value="TEST COMPLETED/FB PENDING">TEST COMPLETED/FB PENDING</option>
                    <option value="TEST SELECT,YET TO SCHEDULE L1">TEST SELECT,YET TO SCHEDULE L1</option>
                    <option value="TEST REJECT">TEST REJECT</option>
                  </optgroup>
                  <optgroup label="L1 Interviews">
                    <option value="INTERVIEW-YTS( L-1)">INTERVIEW-YTS( L-1)</option>
                    <option value="LEVEL 1 - SCHEDULED">LEVEL 1 - SCHEDULED</option>
                    <option value="L1 Selects">L1 Selects</option>
                    <option value="L1 Rejects">L1 Rejects</option>
                  </optgroup>
                  <optgroup label="L2 Interviews">
                    <option value="L2 Selects">L2 Selects</option>
                    <option value="L2 Rejects">L2 Rejects</option>
                  </optgroup>
                  <optgroup label="Offer & Onboarding">
                    <option value="Offered">Offered</option>
                    <option value="Offer Drop">Offer Drop</option>
                    <option value="Joined">Joined</option>
                  </optgroup>
                </select>
              </div>
            </div>
          </div>

          {/* Overall Recommendation Banner - Top Section */}
          {overallRecommendation && (() => {
            const getRecommendationType = (recommendation: string) => {
              const text = recommendation.toLowerCase();
              
              if (text.includes('highly recommended') || text.includes('strong match') || text.includes('excellent fit')) {
                return 'highly_recommended';
              } else if (text.includes('recommended') && !text.includes('not recommended')) {
                return 'recommended';
              } else if (text.includes('conditionally recommended') || text.includes('consider with caution') || text.includes('partial match')) {
                return 'conditional';
              } else if (text.includes('not recommended') || text.includes('poor fit')) {
                return 'not_recommended';
              } else {
                return 'neutral';
              }
            };

            const getRecommendationColors = (type: string) => {
              switch (type) {
                case 'highly_recommended':
                  return {
                    border: 'border-green-300 dark:border-green-600',
                    background: 'bg-gradient-to-r from-green-50/90 to-emerald-50/80 dark:from-green-900/30 dark:to-emerald-900/20',
                    iconBg: 'bg-green-500/30',
                    icon: 'text-green-600',
                    text: 'text-green-800 dark:text-green-200',
                    shadow: 'shadow-lg shadow-green-500/20'
                  };
                case 'recommended':
                  return {
                    border: 'border-green-200 dark:border-green-700',
                    background: 'bg-gradient-to-r from-green-50/80 to-green-100/60 dark:from-green-900/20 dark:to-green-800/30',
                    iconBg: 'bg-green-500/20',
                    icon: 'text-green-500',
                    text: 'text-green-700 dark:text-green-300',
                    shadow: 'shadow-md shadow-green-500/10'
                  };
                case 'conditional':
                  return {
                    border: 'border-amber-200 dark:border-amber-700',
                    background: 'bg-gradient-to-r from-amber-50/80 to-yellow-50/60 dark:from-amber-900/20 dark:to-yellow-900/30',
                    iconBg: 'bg-amber-500/20',
                    icon: 'text-amber-500',
                    text: 'text-amber-700 dark:text-amber-300',
                    shadow: 'shadow-md shadow-amber-500/10'
                  };
                case 'not_recommended':
                  return {
                    border: 'border-red-200 dark:border-red-700',
                    background: 'bg-gradient-to-r from-red-50/80 to-red-100/60 dark:from-red-900/20 dark:to-red-800/30',
                    iconBg: 'bg-red-500/20',
                    icon: 'text-red-500',
                    text: 'text-red-700 dark:text-red-300',
                    shadow: 'shadow-md shadow-red-500/10'
                  };
                default:
                  return {
                    border: 'border-gray-200 dark:border-gray-700',
                    background: 'bg-gradient-to-r from-gray-50/80 to-gray-100/60 dark:from-gray-900/20 dark:to-gray-800/30',
                    iconBg: 'bg-gray-500/20',
                    icon: 'text-gray-500',
                    text: 'text-gray-700 dark:text-gray-300',
                    shadow: 'shadow-sm'
                  };
              }
            };

            const getRecommendationIcon = (type: string) => {
              switch (type) {
                case 'highly_recommended':
                case 'recommended':
                  return <CheckCircle className="w-8 h-8 text-green-500" />;
                case 'conditional':
                  return <AlertTriangle className="w-8 h-8 text-amber-500" />;
                case 'not_recommended':
                  return <XCircle className="w-8 h-8 text-red-500" />;
                default:
                  return <Info className="w-8 h-8 text-gray-500" />;
              }
            };

            const recommendationType = getRecommendationType(overallRecommendation);
            const colors = getRecommendationColors(recommendationType);
            const icon = getRecommendationIcon(recommendationType);

            return (
              <div className="relative z-10 p-6 lg:p-8">
                <Card className={`glass-card backdrop-blur-xl border-2 shadow-2xl ${colors.border} ${colors.background} ${colors.shadow}`}>
                  <div className="p-6 lg:p-8">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`p-3 rounded-xl mr-4 ${colors.iconBg}`}>
                          {icon}
                        </div>
                        <div>
                          <h3 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary">
                            Overall Recommendation
                          </h3>
                          <p className={`text-lg font-semibold ${colors.text}`}>
                            {overallRecommendation}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            );
          })()}

          {/* At-a-Glance Compact Overview */}
          {analysisResults && (
            <div className="relative z-10 px-6 lg:px-8 pt-4">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="text-lg lg:text-xl font-bold text-text-enterprise-primary">JD vs CV Overview</h2>
                {/* Removed view toggle buttons - now single view */}
              </div>

              {/* KPI Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {/* Overall Fitment Ring */}
                <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-2xl p-5 relative overflow-hidden group">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative w-24 h-24 mx-auto rounded-full p-[7px]" style={ringStyle(analysisResults.overall_fitment_score, 'rgba(34,197,94,0.9)')}>
                    <div className="w-full h-full rounded-full bg-white/60 dark:bg-slate-900/60 backdrop-blur" />
                  </div>
                  <div className="text-center mt-3">
                    <div className="text-2xl font-extrabold text-text-enterprise-primary">{analysisResults.overall_fitment_score}%</div>
                    <div className="text-sm text-text-enterprise-tertiary">Overall</div>
                  </div>
                </div>

                {[
                  { label: 'Skills', value: analysisResults.parameter_analysis.skills.score, color: 'rgba(59,130,246,0.9)' },
                  { label: 'Experience', value: analysisResults.parameter_analysis.experience.score, color: 'rgba(234,179,8,0.9)' },
                  { label: 'Education', value: analysisResults.parameter_analysis.education.score, color: 'rgba(168,85,247,0.9)' },
                  { label: 'Optional Skills', value: analysisResults.parameter_analysis.optional_skills.score, color: 'rgba(16,185,129,0.9)' }
                ].map((kpi, idx) => (
                  <div key={`kpi-${idx}`} className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-2xl p-5">
                    <div className="w-16 h-16 mx-auto rounded-full p-[6px]" style={ringStyle(Number(kpi.value || 0), kpi.color)}>
                      <div className="w-full h-full rounded-full bg-white/60 dark:bg-slate-900/60" />
                    </div>
                    <div className="text-center mt-3">
                      <div className="text-lg font-bold text-text-enterprise-primary">{kpi.value}%</div>
                      <div className="text-sm text-text-enterprise-tertiary">{kpi.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Highlights Row - Optimized layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                {/* Skills Matrix - Expanded */}
                <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-base font-bold text-text-enterprise-primary">Skills Matrix</div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-text-enterprise-tertiary">{skillsTableRows.filter(r => r.cv_match).length} matched / {skillsTableRows.length}</div>
                      <div className="w-2 h-2 rounded-full bg-success-500 animate-pulse" />
                    </div>
                  </div>
                  <div className="max-h-64 overflow-auto pr-2 custom-scrollbar">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-text-enterprise-tertiary border-b border-glass-border/50">
                          <th className="text-left py-2 pr-3 font-semibold">Skill</th>
                          <th className="text-left py-2 pr-3 font-semibold">Type</th>
                          <th className="text-left py-2 pr-3 font-semibold">Experience</th>
                          <th className="text-left py-2 font-semibold">Match</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(skillsTableRows.slice(0, 12)).map((row, i) => (
                          <tr key={`skill-${i}`} className="border-b border-glass-border/30 hover:bg-glass-surface/30 transition-colors">
                            <td className="py-2.5 pr-3 text-text-enterprise-primary font-medium">{row.jd_skill_name}</td>
                            <td className="py-2.5 pr-3">
                              <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                                row.type === 'mandatory' ? 'bg-error-500/20 text-error-600 dark:text-error-400 border border-error-500/30' : 'bg-warning-500/20 text-warning-600 dark:text-warning-400 border border-warning-500/30'
                              }`}>
                                {row.type === 'mandatory' ? 'Required' : 'Optional'}
                              </span>
                            </td>
                            <td className="py-2.5 pr-3 text-text-enterprise-secondary text-xs">
                              {row.experience || 'N/A'}
                            </td>
                            <td className="py-2.5">
                              <div className="flex items-center gap-1">
                                <div className={`w-3 h-3 rounded-full ${row.cv_match ? 'bg-success-500' : 'bg-error-500'}`} />
                                <span className="text-xs text-text-enterprise-tertiary">
                                  {row.cv_match ? 'Yes' : 'No'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {skillsTableRows.length === 0 && (
                          <tr>
                            <td colSpan={4} className="py-8 text-center text-text-enterprise-tertiary">
                              <div className="flex flex-col items-center">
                                <div className="w-12 h-12 rounded-full bg-glass-surface/50 flex items-center justify-center mb-3">
                                  <span className="text-2xl">🔧</span>
                                </div>
                                <p className="font-medium">No skills data available</p>
                                <p className="text-xs mt-1">Skills will appear after analysis</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {skillsTableRows.length > 12 && (
                    <div className="mt-3 text-xs text-text-enterprise-tertiary text-center">
                      +{skillsTableRows.length - 12} more skills
                    </div>
                  )}
                </div>

                {/* L1 Technical Questions - Expanded */}
                <div className="glass-card backdrop-blur-xl border-glass-border shadow-2xl rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-base font-bold text-text-enterprise-primary">L1 Technical Questions</div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-text-enterprise-tertiary">{screeningQuestions.length} questions</div>
                      <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                    </div>
                  </div>
                  <div className="max-h-64 overflow-auto pr-2 custom-scrollbar">
                    <ul className="space-y-3">
                      {(screeningQuestions.slice(0, 10)).map((q, i) => (
                        <li key={`q-${i}`} className="text-sm text-text-enterprise-secondary flex items-start gap-3 p-3 rounded-lg bg-glass-surface/30 hover:bg-glass-surface/50 transition-colors">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-xs font-bold text-primary-600 dark:text-primary-400 mt-0.5">
                            {i + 1}
                          </div>
                          <div className="flex-1">
                            <p className="leading-relaxed">{q}</p>
                          </div>
                        </li>
                      ))}
                      {screeningQuestions.length === 0 && (
                        <li className="text-sm text-text-enterprise-tertiary text-center py-8">
                          <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-glass-surface/50 flex items-center justify-center mb-3">
                              <span className="text-2xl">❓</span>
                            </div>
                            <p className="font-medium">No questions available</p>
                            <p className="text-xs mt-1">Questions will appear after analysis</p>
                          </div>
                        </li>
                      )}
                    </ul>
                  </div>
                  {screeningQuestions.length > 10 && (
                    <div className="mt-3 text-xs text-text-enterprise-tertiary text-center">
                      +{screeningQuestions.length - 10} more questions
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}


          {/* Main Content - Enterprise Layout */}
          <div className="relative z-10 p-6 lg:p-8 space-y-6 lg:space-y-8">

            {/* Candidate Details & Fitment Analysis - Side by Side */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
              {/* Candidate Details */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl">
                <div className="p-6 lg:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary flex items-center">
                      <div className="p-2 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl mr-3">
                        <Users className="w-6 h-6 text-blue-500" />
                      </div>
                      Candidate Details
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {/* Name */}
                    <div className="flex items-center justify-between py-3 border-b border-glass-border">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Name</span>
                      <span className="text-base font-medium text-text-enterprise-primary">{candidateData?.name || 'John Doe'}</span>
                    </div>
                    
                    {/* Email */}
                    <div className="flex items-center justify-between py-3 border-b border-glass-border">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Email</span>
                      <span className="text-base font-medium text-text-enterprise-primary">{candidateData?.email || 'john.doe@email.com'}</span>
                    </div>
                    
                    {/* Phone */}
                    <div className="flex items-center justify-between py-3 border-b border-glass-border">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Phone</span>
                      <span className="text-base font-medium text-text-enterprise-primary">{candidateData?.phone || '+91 98765 43210'}</span>
                    </div>
                    
                    {/* Read-only fields */}
                    <div className="flex items-center justify-between py-3 border-b border-glass-border">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Current Designation</span>
                      <span className="text-base font-medium text-text-enterprise-primary">{candidateData?.current_designation || 'Senior Frontend Developer'}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-glass-border">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Location</span>
                      <span className="text-base font-medium text-text-enterprise-primary">{candidateData?.location || 'Bangalore, India'}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-glass-border">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Experience</span>
                      <span className="text-base font-medium text-text-enterprise-primary">{candidateData?.experience || '3 years'}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b border-glass-border">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Education</span>
                      <span className="text-base font-medium text-text-enterprise-primary">{candidateData?.education || 'B.Tech Computer Science'}</span>
                    </div>
                    
                    {/* Edit action buttons removed */}
                    
                    <div className="py-4">
                      <span className="text-base font-semibold text-text-enterprise-secondary block mb-3">Summary</span>
                      <p className="text-base text-text-enterprise-tertiary leading-relaxed">
                        {candidateData?.summary || 'Experienced Frontend Developer with 3+ years of expertise in React.js, JavaScript, and modern web technologies. Proven track record of delivering high-quality user interfaces and collaborating effectively with cross-functional teams. Strong problem-solving skills and passion for creating responsive, scalable web applications.'}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Fitment Analysis */}
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl">
                <div className="p-6 lg:p-8">
                  <h3 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary mb-6 flex items-center">
                    <div className="p-2 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl mr-3">
                      <Target className="w-6 h-6 text-green-500" />
                    </div>
                    Fitment Analysis
                  </h3>
                  
                  {/* Overall Score */}
                  <div className="text-center mb-8">
                    <div className={`inline-flex items-center justify-center w-24 h-24 lg:w-28 lg:h-28 rounded-full ${getScoreBgColor(analysisResults.overall_fitment_score)} mb-4 shadow-2xl`}>
                      <span className={`text-3xl lg:text-4xl font-bold ${getScoreColor(analysisResults.overall_fitment_score)}`}>
                        {analysisResults.overall_fitment_score}
                      </span>
                    </div>
                    <h4 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary mb-2">Overall Fitment Score</h4>
                    <p className="text-base lg:text-lg text-text-enterprise-secondary font-medium">
                      {analysisResults.overall_fitment_score >= 80 ? 'Excellent Match' :
                       analysisResults.overall_fitment_score >= 60 ? 'Good Match' : 'Needs Improvement'}
                    </p>
                  </div>

                  {/* Weighted Parameters */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 mb-4">
                      <h5 className="text-base lg:text-lg font-semibold text-text-enterprise-secondary">Score Breakdown</h5>
                      <div className="relative group">
                        <Info className="w-5 h-5 text-text-enterprise-tertiary cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-3 glass-card text-sm rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                        <div className="space-y-1">
                            <div>Mandatory Skills: 60%</div>
                            <div>Experience: 25%</div>
                            <div>Education: 15%</div>
                        </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-glass-border"></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Mandatory Skills */}
                    <div className="flex items-center justify-between py-3 px-4 bg-gradient-to-r from-red-50/80 to-red-100/60 dark:from-red-900/20 dark:to-red-800/30 rounded-xl backdrop-blur-sm border border-red-200/50 dark:border-red-700/50">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Mandatory Skills</span>
                      <span className="text-base font-bold text-text-enterprise-primary">{analysisResults.parameter_analysis.skills.score}%</span>
                    </div>
                    
                    {/* Experience */}
                    <div className="flex items-center justify-between py-3 px-4 bg-gradient-to-r from-orange-50/80 to-orange-100/60 dark:from-orange-900/20 dark:to-orange-800/30 rounded-xl backdrop-blur-sm border border-orange-200/50 dark:border-orange-700/50">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Experience</span>
                      <span className="text-base font-bold text-text-enterprise-primary">{analysisResults.parameter_analysis.experience.score}%</span>
                    </div>
                    
                    {/* Education */}
                    <div className="flex items-center justify-between py-3 px-4 bg-gradient-to-r from-blue-50/80 to-blue-100/60 dark:from-blue-900/20 dark:to-blue-800/30 rounded-xl backdrop-blur-sm border border-blue-200/50 dark:border-blue-700/50">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Education</span>
                      <span className="text-base font-bold text-text-enterprise-primary">{analysisResults.parameter_analysis.education.score}%</span>
                    </div>
                    
                    {/* Domain */}
                    {/* Optional Skills */}
                    <div className="flex items-center justify-between py-3 px-4 bg-gradient-to-r from-green-50/80 to-green-100/60 dark:from-green-900/20 dark:to-green-800/30 rounded-xl backdrop-blur-sm border border-green-200/50 dark:border-green-700/50">
                      <span className="text-base font-semibold text-text-enterprise-secondary">Optional Skills</span>
                      <span className="text-base font-bold text-text-enterprise-primary">{analysisResults.parameter_analysis.optional_skills.score}%</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Skills Match Analysis Table */}
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl">
              <div className="p-6 lg:p-8">
                    <div className="flex items-center gap-3 mb-8">
                      <h3 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary flex items-center">
                        <div className="p-2 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl mr-3">
                          <Award className="w-6 h-6 text-yellow-500" />
                        </div>
                        Skills Match Analysis
                      </h3>
                      <div className="relative group">
                        <Info className="w-5 h-5 text-text-enterprise-tertiary cursor-help" onClick={() => setShowWeightInfo(!showWeightInfo)} />
                        {showWeightInfo && (
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-3 glass-card text-sm rounded-xl whitespace-nowrap z-10">
                            <div className="space-y-1 text-left">
                              <div className="font-semibold text-text-enterprise-primary">Weights (sum = 100%)</div>
                              <div className="text-text-enterprise-secondary">Mandatory total: 90%</div>
                              {skillsWeightAllocation.filter(w => w.type === 'mandatory').map((w, i) => (
                                <div key={`mw-${i}`} className="text-text-enterprise-tertiary">• {w.skill}: {w.weight_percent}%</div>
                              ))}
                              <div className="mt-1 text-text-enterprise-secondary">Optional total: 10%</div>
                              {skillsWeightAllocation.filter(w => w.type === 'optional').map((w, i) => (
                                <div key={`ow-${i}`} className="text-text-enterprise-tertiary">• {w.skill}: {w.weight_percent}%</div>
                              ))}
                            </div>
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-glass-border"></div>
                          </div>
                        )}
                      </div>
                    </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-slate-50/80 to-slate-100/60 dark:from-slate-800/80 dark:to-slate-700/60 backdrop-blur-sm">
                        <th className="text-left p-4 lg:p-6 text-base font-bold text-text-enterprise-primary border-b border-glass-border">JD Skill Name</th>
                        <th className="text-left p-4 lg:p-6 text-base font-bold text-text-enterprise-primary border-b border-glass-border">Type</th>
                        <th className="text-left p-4 lg:p-6 text-base font-bold text-text-enterprise-primary border-b border-glass-border">CV Match</th>
                        <th className="text-left p-4 lg:p-6 text-base font-bold text-text-enterprise-primary border-b border-glass-border">Experience</th>
                        <th className="text-left p-4 lg:p-6 text-base font-bold text-text-enterprise-primary border-b border-glass-border">Score</th>
                        <th className="text-left p-4 lg:p-6 text-base font-bold text-text-enterprise-primary border-b border-glass-border">Level</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skillsTableRows.length > 0 ? skillsTableRows.map((row, idx) => (
                        <tr key={`skill-row-${idx}`} className={row.type === 'mandatory' ? 'hover:bg-red-50/50 dark:hover:bg-red-900/10 transition-colors duration-200' : 'hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors duration-200'}>
                          <td className="p-4 lg:p-6 text-base font-semibold text-text-enterprise-primary border-b border-glass-border">{row.jd_skill_name}</td>
                          <td className="p-4 lg:p-6">
                            <span className={row.type === 'mandatory' ? 'px-3 py-1.5 bg-gradient-to-r from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/40 text-red-800 dark:text-red-200 text-sm rounded-lg font-semibold' : 'px-3 py-1.5 bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/40 text-blue-800 dark:text-blue-200 text-sm rounded-lg font-semibold'}>
                              {row.type === 'mandatory' ? 'Mandatory' : 'Optional'}
                            </span>
                          </td>
                          <td className={row.cv_match ? 'p-4 lg:p-6 text-green-600 dark:text-green-400 font-semibold text-base' : 'p-4 lg:p-6 text-red-600 dark:text-red-400 font-semibold text-base'}>
                            {row.cv_match ? `✓ ${row.cv_skill_name || row.jd_skill_name}` : '✗ Not Found'}
                          </td>
                          <td className="p-4 lg:p-6 text-text-enterprise-tertiary text-base">{row.experience || '-'}</td>
                          <td className="p-4 lg:p-6">
                            <span className={(row.cv_match ? 'px-3 py-1.5 bg-gradient-to-r from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/40 text-green-800 dark:text-green-200' : 'px-3 py-1.5 bg-gradient-to-r from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/40 text-red-800 dark:text-red-200') + ' text-base font-bold rounded-lg'}>
                              {row.score}%
                            </span>
                          </td>
                          <td className="p-4 lg:p-6 text-text-enterprise-primary text-base font-medium">{row.level || '-'}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-gray-500 dark:text-gray-400">
                            <div className="flex flex-col items-center">
                              <Info className="w-8 h-8 mb-2 opacity-50" />
                              <p className="text-base">No skills data available</p>
                              <p className="text-sm mt-1">Skills analysis may not be applicable for this candidate</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 flex items-center justify-between text-base">
                  {(() => {
                    const rows = skillsTableRows.length ? skillsTableRows : [];
                    const matched = rows.filter(r => r.cv_match).length;
                    const missing = rows.filter(r => !r.cv_match).length;
                    return (
                      <>
                        <div className="flex items-center text-green-600 dark:text-green-400">
                          <CheckCircle className="w-5 h-5 mr-2" />
                          <span className="font-semibold">{matched} skills matched</span>
                        </div>
                        <div className="flex items-center text-red-600 dark:text-red-400">
                          <XCircle className="w-5 h-5 mr-2" />
                          <span className="font-semibold">{missing} skills missing</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Match Justification (only if gaps exist) */}
                {(fitmentJustification || experienceAssessment === 'underqualified') && (
                  <div className="mt-4 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                    <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-2 flex items-center">
                      <Info className="w-4 h-4 mr-2" />
                      Match Justification
                    </h4>
                    <p className="text-sm text-orange-700 dark:text-orange-300 leading-relaxed">
                      {fitmentJustification || 'Candidate is under the required experience threshold; proceed only if rapid ramp-up is feasible and other parameters are exceptionally strong.'}
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Experience & Education Analysis */}
            <Card className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                  Experience & Education Analysis
                </h3>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800">
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Attribute</th>
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">JD Required</th>
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">CV Found</th>
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Match Status</th>
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Score</th>
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors duration-200">
                        <td className="p-4 text-gray-900 dark:text-white font-medium border-b border-gray-100 dark:border-gray-800">Experience</td>
                        <td className="p-4 text-gray-600 dark:text-gray-400">{requirementData?.experience?.minYears ? `${requirementData.experience.minYears}+ years` : 'Not specified'}</td>
                        <td className="p-4 text-orange-600 dark:text-orange-400 font-medium">{candidateData?.parsed_fields?.experience_years ? `${candidateData.parsed_fields.experience_years} years` : 'Not specified'}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs rounded font-medium ${
                            experienceAssessment === 'qualified' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            experienceAssessment === 'underqualified' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                            'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                          }`}>
                            {experienceAssessment === 'qualified' ? '✅ Qualified' : 
                             experienceAssessment === 'underqualified' ? '❌ Underqualified' : 
                             experienceAssessment === 'overqualified' ? '⚠️ Overqualified' : '❓ Unknown'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-sm font-semibold rounded ${
                            analysisResults.parameter_analysis.experience.score >= 70 ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            analysisResults.parameter_analysis.experience.score >= 40 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                            'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                          }`}>
                            {analysisResults.parameter_analysis.experience.score}%
                          </span>
                        </td>
                        <td className="p-4 text-xs text-gray-600 dark:text-gray-400">{analysisResults.parameter_analysis.experience.explanation}</td>
                      </tr>
                      <tr className="hover:bg-green-50 dark:hover:bg-green-900/10 transition-colors duration-200">
                        <td className="p-4 text-gray-900 dark:text-white font-medium border-b border-gray-100 dark:border-gray-800">Education</td>
                        <td className="p-4 text-gray-600 dark:text-gray-400">{requirementData?.education || 'Not specified'}</td>
                        <td className="p-4 text-green-600 dark:text-green-400 font-medium">{candidateData?.parsed_fields?.education || 'Not specified'}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs rounded font-medium ${
                            analysisResults.parameter_analysis.education.score >= 70 ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            analysisResults.parameter_analysis.education.score >= 40 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                            'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                          }`}>
                            {analysisResults.parameter_analysis.education.score >= 70 ? '✅ Match' : 
                             analysisResults.parameter_analysis.education.score >= 40 ? '⚠️ Partial Match' : '❌ No Match'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-sm font-semibold rounded ${
                            analysisResults.parameter_analysis.education.score >= 70 ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            analysisResults.parameter_analysis.education.score >= 40 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                            'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                          }`}>
                            {analysisResults.parameter_analysis.education.score}%
                          </span>
                        </td>
                        <td className="p-4 text-xs text-gray-600 dark:text-gray-400">{analysisResults.parameter_analysis.education.explanation}</td>
                      </tr>
                      <tr className="hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors duration-200">
                        <td className="p-4 text-gray-900 dark:text-white font-medium border-b border-gray-100 dark:border-gray-800">Optional Skills</td>
                        <td className="p-4 text-gray-600 dark:text-gray-400">{requirementData?.skills?.optional_skills?.length ? requirementData.skills.optional_skills.join(', ') : 'None specified'}</td>
                        <td className="p-4 text-blue-600 dark:text-blue-400 font-medium">{analysisResults.parameter_analysis.optional_skills.skills_match?.length ? analysisResults.parameter_analysis.optional_skills.skills_match.join(', ') : 'None matched'}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs rounded font-medium ${
                            analysisResults.parameter_analysis.optional_skills.score >= 70 ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            analysisResults.parameter_analysis.optional_skills.score >= 40 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                            'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                          }`}>
                            {analysisResults.parameter_analysis.optional_skills.score >= 70 ? '✅ Good Match' : 
                             analysisResults.parameter_analysis.optional_skills.score >= 40 ? '⚠️ Partial Match' : '❌ Poor Match'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-sm font-semibold rounded ${
                            analysisResults.parameter_analysis.optional_skills.score >= 70 ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                            analysisResults.parameter_analysis.optional_skills.score >= 40 ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                            'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                          }`}>
                            {analysisResults.parameter_analysis.optional_skills.score}%
                          </span>
                        </td>
                        <td className="p-4 text-xs text-gray-600 dark:text-gray-400">{analysisResults.parameter_analysis.optional_skills.explanation}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>

            {/* Previous Experience Details */}
            <Card className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Briefcase className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                  Previous Experience Details
                </h3>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800">
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Position</th>
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Company</th>
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Duration</th>
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Company Type</th>
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Key Achievements</th>
                        <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">Technologies</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidateData?.work_experience && candidateData.work_experience.length > 0 ? (
                        candidateData.work_experience.map((exp: any, index: number) => {
                          console.log('🔍 Frontend Debug - Rendering work experience:', exp);
                          
                          // Handle roles which might be an object with {title, period, highlights, domain}
                          let roleTitle = 'N/A';
                          if (exp.roles) {
                            if (typeof exp.roles === 'string') {
                              roleTitle = exp.roles;
                            } else if (typeof exp.roles === 'object' && exp.roles.title) {
                              roleTitle = exp.roles.title;
                            } else if (Array.isArray(exp.roles)) {
                              roleTitle = exp.roles.map((r: any) => typeof r === 'string' ? r : r.title || r.name || '').filter(Boolean).join(', ');
                            }
                          } else {
                            roleTitle = exp.position || exp.title || exp.job_title || 'N/A';
                          }
                          
                          return (
                          <tr key={index} className="hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors duration-200">
                            <td className="p-4 text-gray-900 dark:text-white font-medium border-b border-gray-100 dark:border-gray-800">
                              {roleTitle}
                            </td>
                            <td className="p-4 text-gray-600 dark:text-gray-400">
                              {exp.organization || exp.company || exp.employer || 'N/A'}
                            </td>
                            <td className="p-4 text-gray-600 dark:text-gray-400">
                              {exp.start_date && exp.end_date ? (
                                <>
                                  {new Date(exp.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} - {exp.end_date === 'Present' || exp.current ? 'Present' : new Date(exp.end_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                  <br/>
                                  <span className="text-xs text-gray-500">
                                    {exp.duration || 'Duration not specified'}
                                  </span>
                                </>
                              ) : (
                                exp.duration || 'N/A'
                              )}
                            </td>
                            <td className="p-4 text-gray-600 dark:text-gray-400">
                              {exp.company_type || exp.industry || exp.sector || 'N/A'}
                            </td>
                            <td className="p-4 text-gray-600 dark:text-gray-400">
                              {exp.technologies ? (
                                <div className="text-sm">
                                  <strong>Technologies:</strong> {typeof exp.technologies === 'string' ? exp.technologies : exp.technologies.join(', ')}
                                </div>
                              ) : exp.achievements && Array.isArray(exp.achievements) ? (
                                <ul className="text-sm space-y-1">
                                  {exp.achievements.slice(0, 3).map((achievement: string, idx: number) => (
                                    <li key={idx}>• {achievement}</li>
                                  ))}
                                </ul>
                              ) : exp.responsibilities && Array.isArray(exp.responsibilities) ? (
                                <ul className="text-sm space-y-1">
                                  {exp.responsibilities.slice(0, 3).map((responsibility: string, idx: number) => (
                                    <li key={idx}>• {responsibility}</li>
                                  ))}
                                </ul>
                              ) : exp.description ? (
                                <div className="text-sm">
                                  {exp.description.length > 100 ? `${exp.description.substring(0, 100)}...` : exp.description}
                                </div>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td className="p-4 text-gray-600 dark:text-gray-400">
                              {exp.technologies ? 
                                (typeof exp.technologies === 'string' ? exp.technologies : 
                                 Array.isArray(exp.technologies) ? exp.technologies.join(', ') : 'N/A') : 
                                exp.skills && Array.isArray(exp.skills) ? 
                                  exp.skills.join(', ') : 
                                  'N/A'
                              }
                            </td>
                          </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-gray-500 dark:text-gray-400">
                            <div className="flex flex-col items-center">
                              <Briefcase className="w-12 h-12 mb-4 text-gray-300 dark:text-gray-600" />
                              <p className="text-lg font-medium mb-2">No Work Experience Data Available</p>
                              <p className="text-sm">Experience details will appear here once the CV analysis is complete.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-4 flex items-center justify-between text-sm">
                  <div className="flex items-center text-blue-600 dark:text-blue-400">
                    <Briefcase className="w-4 h-4 mr-2" />
                    <span className="font-medium">
                      Total Experience: {candidateData?.experience || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    <span className="font-medium">
                      {candidateData?.work_experience?.length || 0} companies, {candidateData?.work_experience?.length > 0 ? 'experience available' : 'no experience data'}
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Key Strengths */}
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl">
              <div className="p-6 lg:p-8">
                <h3 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary mb-8 flex items-center">
                  <div className="p-2 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl mr-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                  Key Strengths
                </h3>
                {analysisResults?.strengths && analysisResults.strengths.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      {analysisResults.strengths.slice(0, Math.ceil(analysisResults.strengths.length / 2)).map((strength, index) => (
                        <div key={`strength-${index}`} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                          <span className="text-base text-text-enterprise-tertiary font-medium">{strength}</span>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-4">
                      {analysisResults.strengths.slice(Math.ceil(analysisResults.strengths.length / 2)).map((strength, index) => (
                        <div key={`strength-${index + Math.ceil(analysisResults.strengths.length / 2)}`} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                          <span className="text-base text-text-enterprise-tertiary font-medium">{strength}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-glass-surface/50 flex items-center justify-center mb-4">
                        <CheckCircle className="w-8 h-8 text-green-500/50" />
                      </div>
                      <p className="text-lg font-medium text-text-enterprise-tertiary mb-2">No Strengths Data Available</p>
                      <p className="text-sm text-text-enterprise-tertiary">Strengths will appear after analysis is complete</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Areas of Concern */}
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl">
              <div className="p-6 lg:p-8">
                <h3 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary mb-8 flex items-center">
                  <div className="p-2 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-xl mr-3">
                    <XCircle className="w-6 h-6 text-red-500" />
                  </div>
                  Areas of Concern
                </h3>
                {analysisResults?.concerns && analysisResults.concerns.length > 0 ? (
                  <div className="space-y-4">
                    {analysisResults.concerns.map((concern, index) => (
                      <div key={`concern-${index}`} className="bg-gradient-to-r from-red-50/80 to-pink-100/60 dark:from-red-900/20 dark:to-pink-800/30 rounded-xl p-6 border border-red-200/50 dark:border-red-700/50 backdrop-blur-sm">
                        <div className="flex items-start">
                          <XCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="text-base font-semibold text-red-800 dark:text-red-200 mb-2">Concern #{index + 1}</h4>
                            <p className="text-base text-red-700 dark:text-red-300 leading-relaxed">{concern}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-glass-surface/50 flex items-center justify-center mb-4">
                        <XCircle className="w-8 h-8 text-red-500/50" />
                      </div>
                      <p className="text-lg font-medium text-text-enterprise-tertiary mb-2">No Concerns Identified</p>
                      <p className="text-sm text-text-enterprise-tertiary">This is a positive sign - no major concerns found in the analysis</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Salary Negotiation Information */}
            {salaryNegotiation && (
              <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl">
                <div className="p-6 lg:p-8">
                  <h3 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary mb-6 flex items-center">
                    <div className="p-2 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl mr-3">
                      <TrendingUp className="w-6 h-6 text-yellow-500" />
                    </div>
                    Salary Negotiation
                  </h3>
                  <div className="bg-gradient-to-r from-yellow-50/80 to-orange-100/60 dark:from-yellow-900/20 dark:to-orange-800/30 rounded-xl p-6 border border-yellow-200/50 dark:border-yellow-700/50 backdrop-blur-sm">
                    <p className="text-base text-yellow-800 dark:text-yellow-200 leading-relaxed">
                      {salaryNegotiation}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Recruiter Recommendations */}
            <Card className="glass-card backdrop-blur-xl border-glass-border shadow-2xl">
              <div className="p-6 lg:p-8">
                <h3 className="text-xl lg:text-2xl font-bold text-text-enterprise-primary mb-8 flex items-center">
                  <div className="p-2 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-xl mr-3">
                    <Star className="w-6 h-6 text-blue-500" />
                  </div>
                  Recruiter Recommendations
                </h3>
                {analysisResults?.recommendations && analysisResults.recommendations.length > 0 ? (
                  <div className="space-y-6">
                    {analysisResults.recommendations.map((recommendation, index) => (
                      <div key={`recommendation-${index}`} className="bg-gradient-to-r from-blue-50/80 to-indigo-100/60 dark:from-blue-900/20 dark:to-indigo-800/30 rounded-xl p-6 border border-blue-200/50 dark:border-blue-700/50 backdrop-blur-sm">
                        <h4 className="text-base font-bold text-blue-800 dark:text-blue-200 mb-3">Recommendation #{index + 1}</h4>
                        <p className="text-base text-blue-700 dark:text-blue-300 leading-relaxed">{recommendation}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-glass-surface/50 flex items-center justify-center mb-4">
                        <Star className="w-8 h-8 text-blue-500/50" />
                      </div>
                      <p className="text-lg font-medium text-text-enterprise-tertiary mb-2">No Recommendations Available</p>
                      <p className="text-sm text-text-enterprise-tertiary">Recommendations will appear after analysis is complete</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Final Assessment section removed as requested */}
          </div>
        </div>
      </ResponsiveContainer>

      {/* Status Update Modal */}
      {candidateId && requirementId && (
        <StatusUpdateModal
          isOpen={statusUpdateModalOpen}
          onClose={() => {
            setStatusUpdateModalOpen(false);
            setSelectedStatusForUpdate(currentSubmissionStatus);
          }}
          candidateId={candidateId}
          requirementId={requirementId}
          currentStatus={currentSubmissionStatus}
          initialStatus={selectedStatusForUpdate}
          onStatusUpdated={(updatedStatus) => {
            setCurrentSubmissionStatus(updatedStatus);
            setSelectedStatusForUpdate(updatedStatus);
            fetchAnalysisResults();
            toast.success('Status updated successfully!');
          }}
        />
      )}

    </>
  );
};

export default EnhancedJDCVAnalysisPage;
